// Renders a task comment: light markdown (**bold**, *italic*, "- " bullets,
// [text](https://link)) and @[Name](user:id) mentions as pills. Built from React
// nodes only — never HTML — so a comment can't inject markup.
import type { ReactNode } from 'react'

const INLINE_RE =
  /(@\[([^\]\n]{1,120})\]\(user:[0-9a-fA-F-]{36}\))|(\*\*([^*\n]+)\*\*)|(\*([^*\n]+)\*)|(\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\))/g

function renderInline(text: string, keyPrefix = ''): ReactNode[] {
  const nodes: ReactNode[] = []
  let last = 0
  let index = 0
  for (const match of text.matchAll(INLINE_RE)) {
    const start = match.index ?? 0
    if (start > last) nodes.push(text.slice(last, start))
    const key = `${keyPrefix}${index++}`
    if (match[1]) {
      nodes.push(
        <span
          key={key}
          data-testid="comment-mention"
          className="rounded bg-[color-mix(in_srgb,var(--color-info)_14%,var(--color-surface))] px-1 font-medium text-[var(--color-text)]"
        >
          @{match[2]}
        </span>,
      )
    } else if (match[3]) {
      nodes.push(<strong key={key}>{match[4]}</strong>)
    } else if (match[5]) {
      nodes.push(<em key={key}>{match[6]}</em>)
    } else if (match[7]) {
      nodes.push(
        <a
          key={key}
          href={match[9]}
          target="_blank"
          rel="noreferrer noopener"
          className="text-[var(--color-info)] underline-offset-2 hover:underline"
        >
          {match[8]}
        </a>,
      )
    }
    last = start + match[0].length
  }
  if (last < text.length) nodes.push(text.slice(last))
  return nodes
}

export function CommentBody({ body }: { body: string }) {
  const blocks: ReactNode[] = []
  let bullets: string[] = []
  const flush = () => {
    if (bullets.length === 0) return
    const items = bullets
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="m-0 list-disc pl-5">
        {items.map((item, i) => (
          <li key={i}>{renderInline(item, `li${blocks.length}-${i}-`)}</li>
        ))}
      </ul>,
    )
    bullets = []
  }
  body.split('\n').forEach((line, i) => {
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line)
    if (bullet) {
      bullets.push(bullet[1])
      return
    }
    flush()
    blocks.push(
      <p key={`p-${i}`} className="m-0 min-h-[1em] whitespace-pre-wrap">
        {renderInline(line, `p${i}-`)}
      </p>,
    )
  })
  flush()
  return <div className="flex flex-col gap-0.5 text-sm">{blocks}</div>
}
